import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Fingerprint,
    QrCode,
    Plus,
    Minus,
    Check,
    X,
    ShieldAlert,
    ArrowLeft,
    ArrowRight,
    Loader2,
    ShoppingBag,
    AlertTriangle,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { BioButton } from "@/components/bio/BioButton";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/format";

const HUELLA = "https://biofoodsoftware.co/images/new/huella_blanca.png";
const BIOFOOD_LOGO = "/biofood-white.png";
const IDLE_MS = 45000;

export default function Kiosk() {
    const { t } = useLang();
    const [params] = useSearchParams();
    const [cafeterias, setCafeterias] = useState([]);
    const [cafeteriaId, setCafeteriaId] = useState(params.get("cafeteria") || "");
    const [step, setStep] = useState(0); // 0 idle,1 identify,2 choose,3 confirm,4 done
    const [roster, setRoster] = useState([]);
    const [catalog, setCatalog] = useState({ products: [], categories: [] });
    const [student, setStudent] = useState(null);
    const [cart, setCart] = useState({});
    const [scanning, setScanning] = useState(false);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        api.get("/kiosk/cafeterias").then(({ data }) => {
            setCafeterias(data);
            if (!cafeteriaId && data.length) setCafeteriaId(data[0].id);
        });
    }, []); // eslint-disable-line

    useEffect(() => {
        if (!cafeteriaId) return;
        api.get("/kiosk/roster", { params: { cafeteria_id: cafeteriaId } }).then(({ data }) => setRoster(data));
        api.get("/kiosk/catalog", { params: { cafeteria_id: cafeteriaId } }).then(({ data }) => setCatalog(data));
    }, [cafeteriaId]);

    const reset = useCallback(() => {
        setStep(0);
        setStudent(null);
        setCart({});
        setResult(null);
        setScanning(false);
    }, []);

    // Inactivity auto-restart
    const bump = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        if (step !== 0) timer.current = setTimeout(reset, IDLE_MS);
    }, [step, reset]);

    useEffect(() => {
        bump();
        return () => timer.current && clearTimeout(timer.current);
    }, [step, cart, student, bump]);

    const items = useMemo(() => Object.values(cart).filter((i) => i.qty > 0), [cart]);
    const total = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);
    const add = (p) => setCart((c) => ({ ...c, [p.id]: { ...p, qty: (c[p.id]?.qty || 0) + 1 } }));
    const dec = (p) => setCart((c) => {
        const qty = (c[p.id]?.qty || 0) - 1;
        const n = { ...c };
        if (qty <= 0) delete n[p.id]; else n[p.id] = { ...c[p.id], qty };
        return n;
    });

    const pick = async (s) => {
        setScanning("loading");
        try {
            const { data } = await api.post("/kiosk/identify", { cafeteria_id: cafeteriaId, student_id: s.id });
            setStudent(data);
            setScanning(false);
            setStep(2);
        } catch {
            setScanning(false);
        }
    };

    const pay = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/kiosk/checkout", {
                cafeteria_id: cafeteriaId,
                student_id: student.id,
                items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
                method: "qr",
            });
            setResult(data);
            setStep(4);
            if (data.ok) setTimeout(reset, 6000);
        } finally {
            setLoading(false);
        }
    };

    const cafeteria = cafeterias.find((c) => c.id === cafeteriaId);

    return (
        <div className="h-screen w-screen bg-bio-hero text-white font-poppins overflow-hidden relative select-none" onClick={bump} onTouchStart={bump}>
            {/* top brand + lang + cafeteria */}
            <div className="absolute top-0 inset-x-0 h-16 flex items-center justify-between px-6 z-20">
                <div className="flex items-center gap-2.5">
                    <img src={BIOFOOD_LOGO} alt="BIOFOOD" className="h-7 w-auto" />
                    {cafeteria && <span className="text-white/60 text-sm hidden sm:inline">· {cafeteria.name}</span>}
                </div>
                <div className="flex items-center gap-3">
                    <LanguageToggle variant="dark" />
                    {step !== 0 && (
                        <button data-testid="kiosk-restart" onClick={reset} className="text-xs font-semibold bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 transition-colors">{t("kiosk.startOver")}</button>
                    )}
                </div>
            </div>

            <AnimatePresence mode="wait">
                {/* Idle */}
                {step === 0 && (
                    <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center text-center px-6">
                        {cafeterias.length > 1 && (
                            <div className="mb-8">
                                <p className="text-white/70 text-sm mb-2">{t("kiosk.selectCafeteria")}</p>
                                <div className="flex flex-wrap gap-2 justify-center">
                                    {cafeterias.map((c) => (
                                        <button key={c.id} data-testid={`kiosk-caf-${c.id}`} onClick={() => setCafeteriaId(c.id)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${cafeteriaId === c.id ? "bg-white text-bio-primary" : "bg-white/15"}`}>{c.name}</button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ repeat: Infinity, duration: 2.5 }}>
                            <img src={HUELLA} alt="" className="h-24 w-24 mb-6 opacity-90" />
                        </motion.div>
                        <h1 className="text-5xl font-extrabold tracking-tight">{t("kiosk.welcome")}</h1>
                        <p className="text-white/75 text-lg mt-3">{cafeteria?.name}</p>
                        <BioButton data-testid="kiosk-start" className="mt-10 bg-white bg-none !text-bio-primary hover:bg-white h-16 px-12 text-lg shadow-2xl" onClick={() => setStep(1)}>
                            {t("kiosk.tap")} <ArrowRight className="h-5 w-5" />
                        </BioButton>
                    </motion.div>
                )}

                {/* Identify */}
                {step === 1 && (
                    <motion.div key="identify" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center px-6 pt-16">
                        <h2 className="text-3xl font-extrabold">{t("kiosk.identify")}</h2>
                        <p className="text-white/70 mt-2">{t("kiosk.identifySub")}</p>
                        {scanning === "loading" ? (
                            <div className="mt-10 flex flex-col items-center"><Loader2 className="h-12 w-12 animate-spin" /></div>
                        ) : (
                            <>
                                <div className="flex gap-6 mt-8 mb-6">
                                    <div className="flex flex-col items-center gap-2"><div className="h-20 w-20 rounded-full bg-white/15 flex items-center justify-center"><Fingerprint className="h-10 w-10" /></div><span className="text-sm text-white/70">{t("pos.scanFinger")}</span></div>
                                    <div className="flex flex-col items-center gap-2"><div className="h-20 w-20 rounded-full bg-white/15 flex items-center justify-center"><QrCode className="h-10 w-10" /></div><span className="text-sm text-white/70">{t("pos.scanQr")}</span></div>
                                </div>
                                <div className="bg-white rounded-bio-lg p-4 w-full max-w-md max-h-[42vh] overflow-y-auto">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-bio-text-muted mb-2 text-center">{t("pos.pickStudent")}</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {roster.map((s) => (
                                            <button key={s.id} data-testid={`kiosk-roster-${s.id}`} onClick={() => pick(s)} className="flex items-center gap-2 rounded-bio border border-bio-border-subtle p-2 hover:border-bio-accent hover:bg-[#EEF7FC] transition-colors text-left">
                                                <img src={s.photo} alt="" className="h-9 w-9 rounded-full bg-bio-bg" />
                                                <div className="min-w-0"><p className="text-xs font-semibold text-bio-text truncate">{s.name}</p><p className="text-[10px] text-bio-text-muted">{s.grade}</p></div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </motion.div>
                )}

                {/* Choose */}
                {step === 2 && student && (
                    <motion.div key="choose" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="h-full flex flex-col pt-16 pb-4 px-4">
                        <div className="flex items-center gap-3 bg-white/10 rounded-bio-card p-3 mb-3">
                            <img src={student.photo} alt="" className="h-12 w-12 rounded-full bg-white/20" />
                            <div className="flex-1 min-w-0"><p className="font-bold truncate">{student.name}</p><p className="text-sm text-white/70">{t("pos.balance")}: {formatUSD(student.balance)}</p></div>
                            {student.restrictions?.length > 0 && <span className="text-xs bg-[#FEF3C7] text-[#B45309] rounded-full px-2.5 py-1 font-semibold flex items-center gap-1"><ShieldAlert className="h-3 w-3" />{student.restrictions.join(", ")}</span>}
                        </div>
                        <h2 className="text-xl font-bold mb-2">{t("kiosk.choose")}</h2>
                        <div className="flex-1 overflow-y-auto bg-white rounded-bio-lg p-3">
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                {catalog.products.map((p) => {
                                    const qty = cart[p.id]?.qty || 0;
                                    return (
                                        <button key={p.id} data-testid={`kiosk-product-${p.id}`} onClick={() => add(p)} className={`relative text-left bg-white rounded-bio-card border-[1.5px] overflow-hidden transition-all active:scale-95 ${qty ? "border-bio-accent" : "border-bio-border-subtle hover:border-bio-accent"}`}>
                                            <img src={p.image} alt={p.name} className="h-20 w-full object-cover" />
                                            {qty > 0 && <span className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-bio-primary text-white text-xs font-bold flex items-center justify-center">{qty}</span>}
                                            <div className="p-2 text-bio-text"><p className="text-xs font-semibold leading-tight line-clamp-2">{p.name}</p><p className="text-bio-primary font-bold text-sm mt-0.5">{formatUSD(p.price)}</p></div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="mt-3 flex items-center gap-3">
                            <div className="flex-1 bg-white/10 rounded-bio px-4 py-3"><span className="text-white/70 text-sm">{t("pos.total")}: </span><span className="font-extrabold text-xl">{formatUSD(total)}</span></div>
                            <BioButton data-testid="kiosk-to-confirm" className="bg-white bg-none !text-bio-primary hover:bg-white h-14 px-8" disabled={!items.length} onClick={() => setStep(3)}>
                                {t("common.continue")} <ArrowRight className="h-5 w-5" />
                            </BioButton>
                        </div>
                    </motion.div>
                )}

                {/* Confirm */}
                {step === 3 && student && (
                    <motion.div key="confirm" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center px-6 pt-16">
                        <h2 className="text-3xl font-extrabold mb-4">{t("kiosk.confirm")}</h2>
                        <div className="bg-white text-bio-text rounded-bio-lg p-5 w-full max-w-md">
                            <div className="flex items-center gap-2 mb-3"><ShoppingBag className="h-5 w-5 text-bio-primary" /><span className="font-bold">{t("kiosk.myCart")}</span></div>
                            <div className="divide-y divide-bio-border-subtle max-h-[38vh] overflow-y-auto">
                                {items.map((i) => (
                                    <div key={i.id} className="flex items-center gap-3 py-2.5">
                                        <img src={i.image} alt="" className="h-10 w-10 rounded-bio object-cover" />
                                        <p className="flex-1 text-sm font-semibold truncate">{i.name}</p>
                                        <div className="flex items-center gap-1.5">
                                            <button onClick={() => dec(i)} className="h-7 w-7 rounded-bio border border-bio-border flex items-center justify-center"><Minus className="h-3.5 w-3.5" /></button>
                                            <span className="w-5 text-center font-bold text-sm">{i.qty}</span>
                                            <button onClick={() => add(i)} className="h-7 w-7 rounded-bio border border-bio-border flex items-center justify-center"><Plus className="h-3.5 w-3.5" /></button>
                                        </div>
                                        <span className="w-16 text-right text-sm font-bold">{formatUSD(i.price * i.qty)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-bio-border-subtle">
                                <span className="font-bold">{t("pos.total")}</span>
                                <span className="text-2xl font-extrabold text-bio-primary">{formatUSD(total)}</span>
                            </div>
                        </div>
                        <div className="flex gap-3 mt-5 w-full max-w-md">
                            <BioButton variant="ghost" className="!text-white hover:!bg-white/10" onClick={() => setStep(2)}><ArrowLeft className="h-5 w-5" /> {t("kiosk.cancel")}</BioButton>
                            <BioButton data-testid="kiosk-pay" className="flex-1 bg-white bg-none !text-bio-primary hover:bg-white h-14 text-base" loading={loading} onClick={pay}>
                                <Fingerprint className="h-5 w-5" /> {t("kiosk.pay")}
                            </BioButton>
                        </div>
                    </motion.div>
                )}

                {/* Done */}
                {step === 4 && result && (
                    <motion.div key="done" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="kiosk-result">
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.1 }} className={`h-28 w-28 rounded-full flex items-center justify-center mb-6 ${result.ok ? "bg-white text-[#0F807B]" : "bg-white text-[#B71C1C]"}`}>
                            {result.ok ? <Check className="h-14 w-14" /> : <X className="h-14 w-14" />}
                        </motion.div>
                        <h1 className="text-4xl font-extrabold">{result.ok ? t("kiosk.done") : t("pos.declined")}</h1>
                        {result.ok ? (
                            <>
                                <p className="text-white/80 text-lg mt-2">{t("kiosk.doneSub")}</p>
                                <div className="mt-6 bg-white/10 rounded-bio-card px-6 py-4">
                                    <p className="text-white/70 text-sm">{t("pos.newBalance")}</p>
                                    <p className="text-3xl font-extrabold">{formatUSD(result.new_balance)}</p>
                                </div>
                                <p className="text-white/50 text-sm mt-6">{t("kiosk.restart")}</p>
                            </>
                        ) : (
                            <>
                                <div className="mt-3 flex items-center gap-2 bg-white/15 rounded-bio px-4 py-2.5"><AlertTriangle className="h-5 w-5" /> {result.message}</div>
                                <BioButton className="mt-8 bg-white bg-none !text-bio-primary hover:bg-white" onClick={() => setStep(2)}>{t("common.back")}</BioButton>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
